import type { AdminV2PublicWorkspaceToolIcon } from '@/admin-v2/workspace/workspace-runtime';
import type { CmsOrdinaryPageAiEntryPolicy } from '@/cms/frontend-adapters/ai/cms-page-ai-entry-policy';
import type { CmsHydratedImageSourceValue } from '@/cms/image-source';
import type { CmsLocale } from '@/cms/types';
import type { CmsWorkspacePreviewImage } from '@/cms/workspace/workspace-preview-image';
import type { WorkspaceHistoryMediaCopy } from '@/components/workspace-history/workspace-history-copy';
import type {
  SiteRuntimeWorkspaceToolCatalogIcon,
  WorkspaceToolMediaMap,
} from '@/site-runtime/workspace-tool-catalog';
import type {
  WorkspaceCanvasCopy,
  WorkspaceRuntimeCopy,
  WorkspaceToastCopy,
} from '../copy';
import type { WorkspaceToolSummaryView, WorkspaceToolView } from './tool-view';

export const WORKSPACE_TOOL_RUNTIME_PATH_PREFIX = '/api/workspace/v2/tools';

export type WorkspaceStudioBlockConfig = {
  eyebrow?: string;
  subtitle?: string;
  title?: string;
  previewImages?: Array<{
    image: CmsHydratedImageSourceValue;
    imageAlt?: string;
  }>;
  workspaceLaunch?: {
    toolIds?: string[];
    defaultToolId?: string;
  };
};

export type WorkspaceStudioGlobalConfig = WorkspaceCanvasCopy &
  WorkspaceToastCopy & {
    toolCategoryLabels: Record<string, string>;
    uploadLabel: string;
    uploadHint: string;
    imageDimensionsRequirementTemplate: string;
    promptLabel: string;
    promptPlaceholder: string;
    promptPolishLabel: string;
    promptPolishUndoLabel: string;
    promptPolishSucceededLabel: string;
    promptPolishFailedLabel: string;
    referenceImagesLabel: string;
    referenceLimitExceededMessage: string;
    resultLabel: string;
    emptyStateTitle: string;
    emptyStateDescription: string;
    submitLabel: string;
    runningLabel: string;
    downloadLabel: string;
    premiumFeatureTooltip: string;
    restoredImageLabel: string;
    currentImageLabel: string;
    uploadTileLabel: string;
    uploadRemoveImageLabel: string;
    uploadRemoveThumbnailLabel: string;
    templatePreviewLabel: string;
    templateUseLabel: string;
    templatePreviewLoadError: string;
    uploadBatchModeTooltip: string;
    batchModeOffBadgeLabel: string;
    batchModeBadgeLabel: string;
    advancedParametersLabel?: string;
    repeatGenerationLabel: string;
    creditsTotalLabel: string;
    creditsUnitLabel: string;
    creditSavingsTemplate: string;
    creditSavingsPopoverTitle: string;
    creditSavingsModelTemplate: string;
    creditSavingsUpgradeLabel: string;
    addReferenceImageLabel: string;
    referenceEmptyText: string;
    referenceImageAlt: string;
    imageFallbackLabel: string;
    toolPickerSearchPlaceholder: string;
    toolPickerClearSearchLabel: string;
    toolPickerCloseLabel: string;
    toolPickerTitle: string;
    toolCatalogGroupSelectLabel: string;
    toolCatalogImageLabel: string;
    toolCatalogVideoLabel: string;
    uploadRequirementsTooltipLabel: string;
    uploadRequirementsAudioLabel: string;
    uploadRequirementsMaxFilesTemplate: string;
    uploadRequirementsVideoDurationTemplate: string;
    uploadRequirementsVideoDurationRangeTemplate: string;
    uploadRequirementsVideoTotalDurationTemplate: string;
    uploadRequirementsVideoSizeTemplate: string;
    uploadRequirementsAudioDurationTemplate: string;
    uploadRequirementsAudioDurationRangeTemplate: string;
    uploadRequirementsAudioTotalDurationTemplate: string;
    uploadRequirementsAudioSizeTemplate: string;
    toolCatalogPreviousPageLabel: string;
    toolCatalogNextPageLabel: string;
    toolCatalogRetryLabel: string;
    toolCatalogLoadingLabel: string;
    toolCatalogPaidBadgeLabel: string;
    toolCatalogRunningDisabledReason: string;
    toolCatalogLoadError: string;
    toolCatalogUnavailableError: string;
    toolCatalogPageBoundToolUnavailableError: string;
    toolCatalogRuntimeUnavailableError: string;
    assetsLabel: string;
    historyItemsCountTemplate: string;
    loadMoreHistoryLabel: string;
    historySmallViewLabel: string;
    historyMediumViewLabel: string;
    historyLargeViewLabel: string;
    historyDetailViewLabel: string;
    selectHistoryLabel: string;
    selectAllHistoryLabel: string;
    clearHistorySelectionLabel: string;
    finishHistorySelectionLabel: string;
    addHistorySelectionLabel: string;
    deleteHistorySelectionLabel: string;
    downloadHistoryItemLabel: string;
    moreHistoryItemActionsLabel: string;
    renameHistoryItemLabel: string;
    renameHistoryItemTitle: string;
    renameHistoryItemDescription: string;
    renameHistoryItemInputLabel: string;
    cancelHistoryActionLabel: string;
    saveHistoryActionLabel: string;
    deleteHistoryItemTitle: string;
    deleteHistoryItemDescriptionTemplate: string;
    confirmDeleteHistoryItemLabel: string;
    closeAssetsLabel: string;
    historyOpenItemLabel: string;
    historyLoadingItemLabel: string;
    historyEmptyTitle: string;
    securityChallengeTitle: string;
    securityChallengeDescription: string;
    securityChallengeError: string;
    calculatorCalculateLabel: string;
    calculatorRecalculateLabel: string;
    calculatorApplyLabel: string;
    calculatorCloseLabel: string;
    calculatorResultTitle: string;
    calculatorEstimatedCreditsTemplate: string;
    calculatorIncompleteMessage: string;
    calculatorStaleMessage: string;
    calculatorUnsupportedMessage: string;
    calculatorInvalidMessage: string;
    calculatorNoChangeMessage: string;
    calculatorApplyFailedMessage: string;
    calculatorAppliedMessage: string;
    promptRequired: string;
    missingReferenceImage: string;
    missingUploadImage: string;
    generationFailed: string;
    contentPolicyBlocked: string;
    genericError: string;
    inputImageDimensionsTooSmall: string;
    upscaleSourceTooSmallForTarget: string;
    failureRefunded: string;
    failurePaidRetryAvailable: string;
    batchFailed: string;
    batchComplete: string;
    missingHistorySourceImage: string;
    jobStatusTimeout: string;
    createJobTimeout: string;
    resultTimeout: string;
    freeConcurrencyLimitMessage: string;
    paidConcurrencyLimitMessage: string;
    toastGenerationSucceeded: string;
    toastContentPolicyBlocked: string;
    toastBatchSucceeded: string;
    toastBatchPartialFailed: string;
    toastUnsupportedFileType: string;
    toastUploadFailed: string;
    toastFileTooLarge: string;
    toastImageTooLargeTemplate: string;
    toastUnsupportedVideoFormat: string;
    toastUnsupportedAudioFormat: string;
    toastVideoTooLargeTemplate: string;
    toastAudioTooLargeTemplate: string;
    toastVideoTotalSizeTooLargeTemplate: string;
    toastAudioTotalSizeTooLargeTemplate: string;
    toastTooManyFiles: string;
    toastTooManyVideosTemplate: string;
    toastTooManyAudioFilesTemplate: string;
    toastVideoDurationTooShortTemplate: string;
    toastVideoDurationTooLongTemplate: string;
    toastAudioDurationTooShortTemplate: string;
    toastAudioDurationTooLongTemplate: string;
    toastVideoTotalDurationTooLongTemplate: string;
    toastAudioTotalDurationTooLongTemplate: string;
    toastVideoResolutionTooLowTemplate: string;
    toastVideoResolutionTooHighTemplate: string;
    toastVideoUnreadable: string;
    toastAudioUnreadable: string;
    toastUploadTimedOut: string;
    toastUploadNetworkError: string;
    toastUploadUnavailable: string;
    toastImageDimensionsTooLarge: string;
    toastHistoryRestored: string;
    toastHistoryAdded: string;
    toastHistoryRenamed: string;
    toastHistoryDeleted: string;
    toastHistoryDeletePartialFailed: string;
    toastDownloadStarted: string;
    toastDownloadUnavailable: string;
    downloading: string;
    loading: string;
    submitting: string;
    queued: string;
    processing: string;
    saving: string;
    batchStatusQueued: string;
    batchStatusProcessing: string;
    batchStatusSucceeded: string;
    batchStatusFailed: string;
    batchManage: string;
    batchManageDone: string;
    batchSelectedCount: string;
    batchSelectAll: string;
    batchClearSelection: string;
    batchDownloadAll: string;
    batchDownloadSelected: string;
    batchDeleteSelected: string;
    batchToggleSelection: string;
    batchRemoveImage: string;
    processingHighTraffic: string;
    uploading: string;
    loaderCanvasLabel: string;
    loaderUploadingDescription: string;
    loaderSubmittingDescription: string;
    loaderQueuedDescription: string;
    loaderQueuedFreeDescription: string;
    loaderQueuedUpgradeCta: string;
    loaderProcessingDescription: string;
    loaderPersistingOutputDescription: string;
    loaderLoadingDescription: string;
    loaderFailedDescription: string;
    loaderTimeoutDescription: string;
    previewUnavailableDescription: string;
    previewRetry: string;
    likeResultAction: string;
    dislikeResultAction: string;
    feedbackSubmitting: string;
    compareAction: string;
    download: string;
    failure: string;
    upgradePlan: string;
    altProcessing: string;
    altEnhanced: string;
    zoomImage: string;
  };

export type WorkspaceStudioGlobalConfigOverride =
  Partial<WorkspaceStudioGlobalConfig>;

export type WorkspaceStudioSurface = 'workspace-page' | 'launched-runtime';

export type WorkspaceToolCatalogGroup = {
  id: string;
  icon: SiteRuntimeWorkspaceToolCatalogIcon;
  label: string;
  description: string;
  toolCount: number;
  tools: WorkspaceToolSummaryView[];
};

export type WorkspaceToolGroupView = {
  id: string;
  label: string;
  tools: Array<{
    toolId: string;
    label: string;
    icon: AdminV2PublicWorkspaceToolIcon;
    badgeLabel: string;
  }>;
};

export type WorkspaceStudioRenderModel = {
  locale: CmsLocale;
  surface: WorkspaceStudioSurface;
  shell: CmsWorkspaceShellModel;
  toolGroups: WorkspaceToolGroupView[];
  toolCatalog?: {
    catalogVersionId: string;
    defaultToolId: string;
    toolMedia?: WorkspaceToolMediaMap;
    groups: WorkspaceToolCatalogGroup[];
    initialToolSource:
      | { kind: 'catalog_default' }
      | { kind: 'page_bound'; boundToolId: string };
  };
  initialAction: InitialWorkspaceActionModel;
};

export type CmsWorkspaceShellModel = {
  hero: {
    eyebrow: string;
    subtitle: string;
    title: string;
    previewImages: CmsWorkspacePreviewImage[];
  };
  controls: {
    uploadLabel: string;
    uploadHint: string;
    imageDimensionsRequirementTemplate: string;
    promptLabel: string;
    promptPlaceholder: string;
    promptPolishLabel: string;
    promptPolishUndoLabel: string;
    promptPolishSucceededLabel: string;
    promptPolishFailedLabel: string;
    referenceImagesLabel: string;
    referenceLimitExceededMessage: string;
    resultLabel: string;
    emptyStateTitle: string;
    emptyStateDescription: string;
    submitLabel: string;
    runningLabel: string;
    downloadLabel: string;
    premiumFeatureTooltip: string;
    restoredImageLabel: string;
    currentImageLabel: string;
    uploadTileLabel: string;
    uploadRemoveImageLabel: string;
    uploadRemoveThumbnailLabel: string;
    uploadBatchModeTooltip: string;
    batchModeOffBadgeLabel: string;
    batchModeBadgeLabel: string;
    advancedParametersLabel?: string;
    repeatGenerationLabel: string;
    creditsTotalLabel: string;
    creditsUnitLabel: string;
    creditSavingsTemplate: string;
    creditSavingsPopoverTitle: string;
    creditSavingsModelTemplate: string;
    creditSavingsUpgradeLabel: string;
    addReferenceImageLabel: string;
    referenceEmptyText: string;
    referenceImageAlt: string;
    imageFallbackLabel: string;
    historyMediaCopy: WorkspaceHistoryMediaCopy;
    toolPickerSearchPlaceholder: string;
    toolPickerClearSearchLabel: string;
    toolPickerCloseLabel: string;
    toolPickerTitle: string;
    toolCatalogGroupSelectLabel: string;
    toolCatalogImageLabel: string;
    toolCatalogVideoLabel: string;
    uploadRequirementsTooltipLabel: string;
    uploadRequirementsAudioLabel: string;
    uploadRequirementsMaxFilesTemplate: string;
    uploadRequirementsVideoDurationTemplate: string;
    uploadRequirementsVideoDurationRangeTemplate: string;
    uploadRequirementsVideoTotalDurationTemplate: string;
    uploadRequirementsVideoSizeTemplate: string;
    uploadRequirementsAudioDurationTemplate: string;
    uploadRequirementsAudioDurationRangeTemplate: string;
    uploadRequirementsAudioTotalDurationTemplate: string;
    uploadRequirementsAudioSizeTemplate: string;
    toolCatalogPreviousPageLabel: string;
    toolCatalogNextPageLabel: string;
    toolCatalogRetryLabel: string;
    toolCatalogLoadingLabel: string;
    toolCatalogRunningDisabledReason: string;
    toolCatalogLoadError: string;
    toolCatalogUnavailableError: string;
    toolCatalogPageBoundToolUnavailableError: string;
    toolCatalogRuntimeUnavailableError: string;
    assetsLabel: string;
    historyItemsCountTemplate: string;
    loadMoreHistoryLabel: string;
    historySmallViewLabel: string;
    historyMediumViewLabel: string;
    historyLargeViewLabel: string;
    historyDetailViewLabel: string;
    selectHistoryLabel: string;
    selectAllHistoryLabel: string;
    clearHistorySelectionLabel: string;
    finishHistorySelectionLabel: string;
    addHistorySelectionLabel: string;
    deleteHistorySelectionLabel: string;
    downloadHistoryItemLabel: string;
    moreHistoryItemActionsLabel: string;
    renameHistoryItemLabel: string;
    renameHistoryItemTitle: string;
    renameHistoryItemDescription: string;
    renameHistoryItemInputLabel: string;
    cancelHistoryActionLabel: string;
    saveHistoryActionLabel: string;
    deleteHistoryItemTitle: string;
    deleteHistoryItemDescriptionTemplate: string;
    confirmDeleteHistoryItemLabel: string;
    closeAssetsLabel: string;
    historyOpenItemLabel: string;
    historyLoadingItemLabel: string;
    templatePreviewLoadError: string;
    historyEmptyTitle: string;
    securityChallengeTitle: string;
    securityChallengeDescription: string;
    securityChallengeError: string;
    calculatorCopy?: Pick<
      WorkspaceStudioGlobalConfig,
      | 'calculatorCalculateLabel'
      | 'calculatorRecalculateLabel'
      | 'calculatorApplyLabel'
      | 'calculatorCloseLabel'
      | 'calculatorResultTitle'
      | 'calculatorEstimatedCreditsTemplate'
      | 'calculatorIncompleteMessage'
      | 'calculatorStaleMessage'
      | 'calculatorUnsupportedMessage'
      | 'calculatorInvalidMessage'
      | 'calculatorNoChangeMessage'
      | 'calculatorApplyFailedMessage'
      | 'calculatorAppliedMessage'
    >;
    templatePickerCopy?: Pick<
      WorkspaceStudioGlobalConfig,
      'templatePreviewLabel' | 'templateUseLabel' | 'templatePreviewLoadError'
    >;
    canvasCopy: WorkspaceCanvasCopy;
  };
  ordinaryPageEntryPolicy: CmsOrdinaryPageAiEntryPolicy;
};

export type InitialWorkspaceActionModel = {
  tools: WorkspaceToolSummaryView[];
  toolViews: WorkspaceToolView[];
  initialToolId: string;
  initialTool: WorkspaceToolView;
  transparentOutputToolIds: string[];
  runtimeCopy: WorkspaceRuntimeCopy;
  toastCopy: WorkspaceToastCopy;
  api: {
    createJobPath: string;
    getJobPathPrefix: string;
    getToolRuntimePathPrefix: string;
  };
};
